/**
 * Pipeline automático de execução de demanda.
 * Roda em sequência: estima → designa → executa → concilia → aprova → entrega.
 * Reporta eventos pra Ops Room.
 */
import { useTasks, type Task } from "../store/tasks";
import { useRH } from "../store/rh";
import { useOps } from "../store/ops";
import { useSettings } from "../store/settings";
import { useNotion } from "../store/notion";
import { useClientBrand, effectiveBrand } from "../store/clientBrand";
import { PERSONAS } from "../data/org";
import { profileOf } from "../data/profiles";
import { clientById } from "../data/clients";
import { notionSearch, formatRagContext } from "./notionRag";
import {
  estimateComplexityAI, suggestAgentsAI, modelForComplexity,
  autoExecuteByAgentAI, mediateConflictAI, tiebreakerAI,
  agentDiscussionAI, type DiscussionTurn
} from "./claude";

export type PipelineStep =
  | "estimate"
  | "suggest"
  | "assign"
  | "discuss"
  | "execute"
  | "mediate"
  | "tiebreaker"
  | "approve"
  | "deliver";

export interface PipelineEvent {
  step: PipelineStep;
  status: "running" | "done" | "skipped" | "error" | "waiting";
  text: string;
  personaId?: string;
  detail?: string;
  /** se step==="discuss", carrega os turnos da conversa */
  discussion?: DiscussionTurn[];
}

export interface PipelineConfig {
  taskId: string;
  /** se true, decide e segue automaticamente sem checkpoint humano */
  autoApprove: boolean;
  /** se true, pula conciliação e voto de minerva */
  skipConsensus: boolean;
  /** se false, pula a discussão entre agentes (mais rápido) */
  enableDiscussion?: boolean;
}

type Emit = (e: PipelineEvent) => void;

/** Roda o pipeline completo (ou até onde der). */
export async function runFullPipeline(cfg: PipelineConfig, emit: Emit): Promise<void> {
  const settings = useSettings.getState();
  if (!settings.anthropicKey) {
    emit({ step: "estimate", status: "error", text: "Configure chave Anthropic em ⚙️." });
    return;
  }

  const get = () => useTasks.getState().tasks.find(t => t.id === cfg.taskId);
  let task = get();
  if (!task) {
    emit({ step: "estimate", status: "error", text: "Task não encontrada." });
    return;
  }

  const update  = useTasks.getState().update;
  const setStage = useTasks.getState().setStage;
  const assign  = useTasks.getState().assign;
  const addOutput = useTasks.getState().addOutput;
  const addApproval = useTasks.getState().addApproval;
  const ops = useOps.getState();

  const client = task.clientId ? clientById(task.clientId) : undefined;
  const ov = task.clientId ? useClientBrand.getState().overrides[task.clientId] : undefined;
  const eff = client ? effectiveBrand(client, ov) : undefined;
  let clientCtx = client && eff
    ? `${client.name} (${client.segment}). Posicionamento: ${eff.pitch}. Público: ${eff.audience}.` +
      (eff.persona ? ` Persona: ${eff.persona}.` : "") +
      (eff.voiceTone ? ` Tom: ${eff.voiceTone}.` : "")
    : undefined;

  // ============= RAG NOTION (busca base de conhecimento se habilitado) =============
  const notion = useNotion.getState();
  if (notion.enabled && notion.injectInGeneration && notion.token) {
    try {
      ops.log("info", "🔎 Buscando contexto no Notion…", { taskId: task.id });
      const query = `${task.title} ${task.description.slice(0, 200)}${client ? " " + client.name : ""}`;
      const hits = await notionSearch(notion.token, query, 5);
      if (hits.length > 0) {
        clientCtx = (clientCtx ?? "") + formatRagContext(hits);
        ops.log("info", `✓ ${hits.length} páginas Notion injetadas no contexto`, { taskId: task.id });
        emit({ step: "estimate", status: "done", text: `Notion RAG: ${hits.length} páginas relevantes` });
      }
    } catch (e) {
      ops.log("info", "✗ Notion search falhou: " + (e as Error).message.slice(0, 80), { taskId: task.id });
    }
  }

  // ============= STEP 1: ESTIMATE =============
  if (!task.complexity) {
    emit({ step: "estimate", status: "running", text: "Estimando complexidade…" });
    try {
      const r = await estimateComplexityAI({
        apiKey: settings.anthropicKey,
        title: task.title, description: task.description,
        targetDepts: task.targetDepts
      });
      update(task.id, { complexity: r.complexity as any });
      emit({ step: "estimate", status: "done", text: `${r.complexity} · ${r.reason}` });
    } catch (e) {
      emit({ step: "estimate", status: "error", text: (e as Error).message });
    }
    task = get()!;
  } else {
    emit({ step: "estimate", status: "skipped", text: `já estimada: ${task.complexity}` });
  }

  // ============= STEP 2: SUGGEST AGENTS =============
  if (task.assignedTo.length === 0) {
    emit({ step: "suggest", status: "running", text: "Time analisando demanda…" });
    try {
      const hired = Object.values(useRH.getState().employees)
        .filter(e => e.status === "hired")
        .map(e => e.personaId);
      if (hired.length === 0) {
        emit({ step: "suggest", status: "error", text: "Sem agentes contratados. Vai no RH primeiro." });
        return;
      }
      const sug = await suggestAgentsAI({
        apiKey: settings.anthropicKey, model: settings.model,
        title: task.title, description: task.description,
        targetDepts: task.targetDepts, hiredAgentIds: hired
      });
      const picks = sug.map(s => s.personaId).slice(0, 3);
      if (picks.length === 0) {
        emit({ step: "suggest", status: "error", text: "Claude não recomendou ninguém." });
        return;
      }
      assign(task.id, picks);
      sug.forEach(s => {
        const p = PERSONAS.find(x => x.id === s.personaId);
        emit({ step: "suggest", status: "done", text: `${p?.name}: ${s.reason}`, personaId: s.personaId });
      });
      ops.log("info", `${picks.length} agente(s) designado(s)`, { taskId: task.id });
    } catch (e) {
      emit({ step: "suggest", status: "error", text: (e as Error).message });
      return;
    }
    task = get()!;
  } else {
    emit({ step: "suggest", status: "skipped", text: `já designados: ${task.assignedTo.length}` });
  }

  // ============= STEP 3: ASSIGN STAGE =============
  setStage(task.id, "delegated");
  emit({ step: "assign", status: "done", text: "Estágio → DELEGADA" });

  // ============= STEP 3.5: DISCUSS (mesa-redonda) =============
  if (cfg.enableDiscussion !== false) {
    emit({ step: "discuss", status: "running", text: "Time discutindo o briefing…" });
    try {
      const participants = task.assignedTo
        .map(id => PERSONAS.find(p => p.id === id))
        .filter((p): p is NonNullable<typeof p> => Boolean(p))
        .map(p => {
          const prof = profileOf(p.id, p.tier);
          return { id: p.id, name: p.name, voice: prof.voice, skills: prof.skills };
        });

      // marcar workers como "thinking" durante discussão
      participants.forEach(p =>
        ops.setWorker(p.id, "thinking", { taskId: task!.id, taskTitle: task!.title, detail: "discutindo briefing" })
      );

      const turns = await agentDiscussionAI({
        apiKey: settings.anthropicKey, model: settings.model,
        taskTitle: task.title, taskDescription: task.description,
        participants, clientContext: clientCtx
      });

      // grava a discussão como output especial pra ficar no histórico
      if (turns.length > 0) {
        const discussionMd =
          `# 🪑 Mesa-redonda — antes da execução\n\n` +
          turns.map(t => {
            const p = PERSONAS.find(x => x.id === t.by);
            const kindIcon =
              t.kind === "question"   ? "❓"
            : t.kind === "objection"  ? "🚫"
            : t.kind === "agreement"  ? "🤝"
            : t.kind === "synthesis"  ? "🎯"
            :                            "💡";
            return `**${kindIcon} ${p?.name ?? t.by}** *(${t.kind})*\n> ${t.text}`;
          }).join("\n\n");
        addOutput(task.id, "marcio", discussionMd, {
          kind: "text", provider: "anthropic", model: "round-table"
        });
        emit({ step: "discuss", status: "done", text: `${turns.length} turnos de discussão`, discussion: turns });
        ops.log("info", `Mesa-redonda concluída · ${turns.length} turnos`, { taskId: task.id });
      } else {
        emit({ step: "discuss", status: "skipped", text: "sem turnos" });
      }
    } catch (e) {
      emit({ step: "discuss", status: "error", text: (e as Error).message });
    }
  } else {
    emit({ step: "discuss", status: "skipped", text: "(desligada)" });
  }

  // ============= STEP 4: EXECUTE =============
  setStage(task.id, "in-progress");
  const assignedNow = task.assignedTo;
  const employees = useRH.getState().employees;
  const routedModel = modelForComplexity(task.complexity, settings.model);

  emit({ step: "execute", status: "running", text: `${assignedNow.length} agente(s) executando em paralelo…` });

  await Promise.all(assignedNow.map(async (pid) => {
    const persona = PERSONAS.find(p => p.id === pid);
    if (!persona) return;
    const prof = profileOf(persona.id, persona.tier);
    const customPrompt = employees[pid]?.systemPromptOverride;

    ops.setWorker(pid, "thinking", { taskId: task!.id, taskTitle: task!.title, detail: "lendo briefing" });
    await new Promise(r => setTimeout(r, 400)); // pausa lúdica
    ops.setWorker(pid, "typing", { taskId: task!.id, taskTitle: task!.title, detail: routedModel });
    ops.log("task-start", `${persona.name} começou`, { personaId: pid, taskId: task!.id });
    emit({ step: "execute", status: "running", text: `${persona.name} digitando…`, personaId: pid });

    try {
      const text = await autoExecuteByAgentAI({
        apiKey: settings.anthropicKey,
        model: routedModel,
        agentName: persona.name,
        agentVoice: prof.voice,
        agentSkills: prof.skills,
        agentExpectations: prof.expectations,
        taskTitle: task!.title,
        taskDescription: task!.description,
        clientContext: clientCtx,
        systemPromptOverride: customPrompt
      });
      try {
        addOutput(task!.id, pid, text, { kind: "text", provider: "anthropic", model: routedModel });
      } catch (storageErr) {
        // se localStorage estourou, auto-compactar e tentar de novo
        useTasks.getState().compact({ maxOutputsPerTask: 3, truncateOutputCharsAbove: 4000, maxTasks: 30 });
        try { addOutput(task!.id, pid, text.slice(0, 4000), { kind: "text", provider: "anthropic", model: routedModel }); } catch {}
      }
      ops.setWorker(pid, "delivered", { taskId: task!.id, taskTitle: task!.title, detail: "entregue" });
      ops.log("task-output", `${persona.name} entregou`, { personaId: pid, taskId: task!.id });
      emit({ step: "execute", status: "done", text: `${persona.name} entregou`, personaId: pid });
    } catch (e) {
      ops.setWorker(pid, "blocked", { detail: (e as Error).message.slice(0, 40) });
      emit({ step: "execute", status: "error", text: `${persona.name}: ${(e as Error).message.slice(0,80)}`, personaId: pid });
    }
  }));

  task = get()!;
  const outs = task.outputs.filter(o => (o.kind ?? "text") === "text");

  // ============= STEP 5: MEDIATE (se ≥2 outputs) =============
  if (!cfg.skipConsensus && outs.length >= 2) {
    emit({ step: "mediate", status: "running", text: "Marcio conciliando…" });
    ops.setWorker("marcio", "reviewing", { taskId: task.id, taskTitle: task.title, detail: "conciliando outputs" });
    try {
      const synth = await mediateConflictAI({
        apiKey: settings.anthropicKey, model: settings.model,
        topic: task.title,
        outputs: outs.map(o => ({ by: PERSONAS.find(p => p.id === o.by)?.name ?? o.by, text: o.text })),
        clientContext: clientCtx
      });
      addOutput(task.id, "marcio", synth, { kind: "text", provider: "anthropic", model: "marcio-consigliere" });
      ops.setWorker("marcio", "delivered", { detail: "síntese pronta" });
      emit({ step: "mediate", status: "done", text: "Marcio entregou síntese conciliada" });
    } catch (e) {
      emit({ step: "mediate", status: "error", text: (e as Error).message });
    }
  } else if (cfg.skipConsensus) {
    emit({ step: "mediate", status: "skipped", text: "(consenso desligado)" });
  } else {
    emit({ step: "mediate", status: "skipped", text: "só 1 output, sem divergência" });
  }

  // ============= STEP 6: TIEBREAKER (Trump) =============
  task = get()!;
  if (!cfg.skipConsensus && task.outputs.length > 0) {
    emit({ step: "tiebreaker", status: "running", text: "Trump batendo o martelo…" });
    ops.setWorker("trump", "reviewing", { taskId: task.id, taskTitle: task.title, detail: "veredito final" });
    try {
      const marcioOut = task.outputs.find(o => o.by === "marcio");
      const verdict = await tiebreakerAI({
        apiKey: settings.anthropicKey, model: "claude-opus-4-7",
        topic: task.title,
        outputs: task.outputs.filter(o => (o.kind ?? "text") === "text")
          .map(o => ({ by: PERSONAS.find(p => p.id === o.by)?.name ?? o.by, text: o.text.slice(0, 800) })),
        marcioMediation: marcioOut?.text,
        clientContext: clientCtx
      });
      addOutput(task.id, "trump", verdict, { kind: "text", provider: "anthropic", model: "trump-tiebreaker" });
      ops.setWorker("trump", "delivered", { detail: "BATIDO." });
      emit({ step: "tiebreaker", status: "done", text: "Trump bateu o martelo" });
    } catch (e) {
      emit({ step: "tiebreaker", status: "error", text: (e as Error).message });
    }
  } else {
    emit({ step: "tiebreaker", status: "skipped", text: "(consenso desligado)" });
  }

  // ============= STEP 7: APPROVE =============
  setStage(task.id, "client-approval");
  if (cfg.autoApprove) {
    addApproval(task.id, {
      stage: "client-approval", by: "marcio", decision: "approved",
      notes: "Aprovação automática via pipeline"
    });
    emit({ step: "approve", status: "done", text: "Aprovação automática" });
  } else {
    emit({ step: "approve", status: "waiting", text: "Aguardando aprovação do Marcio (botões no modal)" });
    return;
  }

  // ============= STEP 8: DELIVER =============
  setStage(task.id, "delivered");
  emit({ step: "deliver", status: "done", text: "✓ Demanda entregue" });
  ops.log("task-done", `Demanda "${task.title}" entregue`, { taskId: task.id });

  // limpar workers após 3s
  setTimeout(() => {
    [...assignedNow, "marcio", "trump"].forEach(pid => useOps.getState().clearWorker(pid));
  }, 3000);
}
